import React from 'react';

import { Alert, Form, Col, Button, InputGroup, Card, Row, Container, Tooltip, OverlayTrigger } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { validate, ValidationError } from 'class-validator';
import { Client, Ethnicity, Gender, User, Menopause } from '../../models';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ClientEntryStore } from '../../stores/client-entry-store';
import { MathUtils } from '../../utils/MathUtils';
import Select from 'react-select';
import { HeightOption, FT_OPTIONS, IN_OPTIONS, HEIGHT_SELECT_STYLES } from './height-select-options';
import { siUnitsVisible } from '../../utils/siUnitsVisibility';

interface ClientEntryProps {
  client: Client;
  currentUser: User;
  clientEntryStore: ClientEntryStore;
  saveOrUpdate: () => Promise<Client>;
  expandAddClient: boolean;
  // The practitioner's Settings default, seeded onto a client that has no
  // Units of its own. Undefined until the profile loads, which is why
  // defaultUnitsFor() still falls back to the last-used value in localStorage.
  defaultUnits?: Units;
  history?: any;
}

type Units = 'US' | 'SI';

/**
 * Which Units radio to pre-select: the client's own saved choice, else the
 * practitioner's Settings default, else whatever they last picked on this
 * device, else US.
 *
 * The Settings step is skipped entirely while SI units are on hold. It has to
 * be: UserProfile normalizes unitSystem to 'US' for every profile, so an
 * ungated read would always match and permanently shadow the localStorage
 * fallback below — silently regressing the height/weight units feature that is
 * already live, by preselecting US for a practitioner who last chose SI.
 *
 * The localStorage step predates the Settings default and stays either way:
 * the profile is not loaded on the very first render, so without it the radio
 * would flash US before settling.
 */
function defaultUnitsFor(clientUnits?: Units, profileDefault?: Units): Units {
  if (clientUnits === 'US' || clientUnits === 'SI') return clientUnits;
  if (
    siUnitsVisible() &&
    (profileDefault === 'US' || profileDefault === 'SI')
  ) {
    return profileDefault;
  }
  const remembered = localStorage.getItem('labsmarts_units') as Units;
  return remembered === 'SI' ? 'SI' : 'US';
}

type State = {
  validationErrors: Map<string, string>;
  alerts: Array<any>,
  expandAddClient: boolean;
  dateOfBirth: string;
  panelTitle: string;
  units: Units;
  heightFt: string;
  heightIn: string;
  lastClientId: string;
};

export class ClientEntry extends React.Component<ClientEntryProps, State> {
  state: State = {
    validationErrors: new Map<string, string>(),
    alerts: [],
    expandAddClient: this.props.expandAddClient,
    dateOfBirth: undefined,
    panelTitle: ClientEntry.panelTitle(this.props.client),
    units: defaultUnitsFor(
      this.props.client?.units as Units,
      this.props.defaultUnits,
    ),
    heightFt: this.props.client?.height != null
      ? String(MathUtils.cmToFtIn(this.props.client.height).ft)
      : '',
    heightIn: this.props.client?.height != null
      ? String(MathUtils.cmToFtIn(this.props.client.height).inches)
      : '',
    lastClientId: this.props.client?.id,
  };

  static getDerivedStateFromProps(nextProps: ClientEntryProps, prevState: State) {
    const { client } = nextProps;

    if (client.cigaretteSmoker == null) client.cigaretteSmoker = false;
    if (client.alcoholDrinker == null) client.alcoholDrinker = false;

    const derived: Partial<State> = {
      dateOfBirth: (client.dateOfBirth ? moment(client.dateOfBirth).toDate() : undefined) as any,
      expandAddClient: nextProps.expandAddClient,
      panelTitle: ClientEntry.panelTitle(client),
    };

    const clientChanged = client.id !== prevState.lastClientId;
    if (clientChanged) {
      derived.lastClientId = client.id;
      const clientUnits = defaultUnitsFor(
        client.units as Units,
        nextProps.defaultUnits,
      );
      derived.units = clientUnits;
      if (client.height != null) {
        if (clientUnits === 'US') {
          const { ft, inches } = MathUtils.cmToFtIn(client.height);
          derived.heightFt = String(ft);
          derived.heightIn = String(inches);
        } else {
          derived.heightFt = '';
          derived.heightIn = '';
        }
      } else {
        derived.heightFt = '';
        derived.heightIn = '';
      }
    }

    return derived;
  }

  static panelTitle(client: Client): string {
    return (client && client.isNew()) ? 'Add Client' : 'Edit Client';
  }

  handleInputChange = (event: any) => {
    const { client } = this.props;
    const key = event.target.name;
    let value = event.target.value;
    let nbr: number;

    if (key === 'weight') {
      client.weight = value.trim().length === 0 ? undefined : Number(value);
      this.clearAlerts();
      return;
    }

    if (key === 'elevation' || key === 'cigarettesSmoked' || key === 'trimester' || key === 'standardDrinks') {
      if (value.trim().length === 0) {
        nbr = undefined;
      } else {
        nbr = Number(value.replace(/\D/g, ''));
      }
    }

    if (key === 'cigaretteSmoker' || key === 'alcoholDrinker') {
      nbr = undefined;
      value = value === 'true';
    }

    if (isNaN(nbr)) {
      client[key] = value;
    } else {
      client[key] = nbr;
    }

    console.log(this.props);
    this.clearAlerts();
  }

  handleHeightFtChange = (opt: HeightOption | null) => {
    const ft = opt?.value ?? '';
    const inches = Number(this.state.heightIn) || 0;
    this.setState({ heightFt: ft });
    if (ft !== '') {
      this.props.client.height = MathUtils.ftInToCm(Number(ft), inches);
    } else if (this.state.heightIn === '') {
      this.props.client.height = undefined;
    }
  };

  handleHeightInChange = (opt: HeightOption | null) => {
    const inches = opt?.value ?? '';
    const ft = Number(this.state.heightFt) || 0;
    this.setState({ heightIn: inches });
    if (inches !== '') {
      this.props.client.height = MathUtils.ftInToCm(ft, Number(inches));
    } else if (this.state.heightFt === '') {
      this.props.client.height = undefined;
    }
  };

  handleHeightCmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cm = e.target.value;
    this.props.client.height = cm.trim() === '' ? undefined : Number(cm);
  };

  handleUnitsChange = (newUnits: Units) => {
    const { units, heightFt, heightIn } = this.state;
    if (newUnits === units) return;

    localStorage.setItem('labsmarts_units', newUnits);

    if (newUnits === 'SI') {
      const ft = Number(heightFt) || 0;
      const inches = Number(heightIn) || 0;
      if (heightFt.trim() !== '' || heightIn.trim() !== '') {
        this.props.client.height = MathUtils.ftInToCm(ft, inches);
      }
      this.setState({ units: 'SI' });
    } else {
      const cm = this.props.client.height;
      if (cm != null) {
        const { ft, inches } = MathUtils.cmToFtIn(cm);
        this.setState({ units: 'US', heightFt: String(ft), heightIn: String(inches) });
      } else {
        this.setState({ units: 'US', heightFt: '', heightIn: '' });
      }
    }
  };

  handleDateOfBirthChange = (value: any) => {
    const { client } = this.props;
    if (value == null) {
      client.dateOfBirth = undefined;
    } else {
      const startOfDay = moment(value).startOf('day').toISOString();
      client.dateOfBirth = startOfDay;
    }

    this.setState({
      dateOfBirth: value
    })

    console.log(client);
  }

  saveOrUpdate = (event: any) => {
    event.preventDefault();
    this.validateAndSaveClient();
  }

  validateAndSaveClient() {
    const { client } = this.props;
    const validationErrors = new Map<string, string>();   
    console.log(client);

    // Special check for pregnant female and age
    if (client.isTooYoungToBePregnant()) {
      validationErrors.set('dateOfBirth', 'Client must be 15 years of age or older when pregnant is selected.');
    }

    // Special check for postmenopausal female and age
    if (client.isTooYoungForPostmenopause()) {
      validationErrors.set('dateOfBirth', 'Client must be 15 years of age or older when postmenopause is selected.');
    }

    // Special check for age
    if (client.isTooOld()) {
      validationErrors.set('dateOfBirth', 'Client can not be over 200 years of age.');
    }

    if (validationErrors.size > 0) {
      this.setState({
        validationErrors
      });
      return;
    }

    validate(client).then((errors: ValidationError[]) => { // errors is an array of validation errors
      if (errors.length > 0 || validationErrors.size > 0) {
        for (let error of errors) {
          this.displayErrorMessage(error, validationErrors);
        }
        this.setState({
          validationErrors
        });
      } else {
        this.setState({
          validationErrors: new Map<string, string>()
        });
        this.saveOrUpdateClient();
      }
    });
  }

  saveOrUpdateClient() {
    this.props.client.units = this.state.units;
    this.props.client.practitionerId = this.props.currentUser.subject;
    // If a non-smoker, set cigarettes smoked to 0.
    if (!this.props.client.cigaretteSmoker) {
      this.props.client.cigarettesSmoked = 0;
    }
    // If a non-drinker, clear standard drinks.
    if (!this.props.client.alcoholDrinker) {
      this.props.client.standardDrinks = undefined;
    }

    this.props.saveOrUpdate().then((updatedClient) => {
      console.log(updatedClient);
      this.clearForm(false);
      this.addAlert('success', 'Successfully updated client!', false, 5000);
      this.props.clientEntryStore.setExpandAddClient(false);
    }).catch((error) => {
      this.addAlert('danger', 'Failed to add/edit client');
    });
  }

  clearForm = (clearAlerts?: boolean) => {
    const { clientEntryStore } = this.props;
    clientEntryStore.resetCurrentClient();

    this.setState({
      dateOfBirth: undefined,
      validationErrors: new Map<string, string>(),
      heightFt: '',
      heightIn: '',
      lastClientId: undefined,
    });

    if (clearAlerts) {
      this.clearAlerts();
    }
  }

  clearAlerts = () => {
    console.log('clearAlerts was called....');
    this.setState({
      alerts: []
    });
  }

  openElevationInfoWindow = () => {
    window.open('https://labsmarts.com/bibliography/#highelevation');
  }

  openStandardDrinkInfoWindow = () => {
    window.open('https://www.niaaa.nih.gov/what-standard-drink');
  }

  displayErrorMessage(error: ValidationError, validationErrors: Map<string, string>) {
    for (let constraint of Object.keys(error.constraints)) {
      validationErrors.set(error.property, error.constraints[constraint]);
    }
  }

  renderErrorListItems(validationErrors: Map<string, string>) {
    let listItems: JSX.Element[] = [];
    validationErrors.forEach((value: string, fieldName: string) => {
      if (validationErrors.get(fieldName)) {
        listItems.push(<li key={fieldName + '-error'}>{validationErrors.get(fieldName)}</li>);
      }
    });

    return listItems;
  }

  renderValidationErrors(validationErrors: Map<string, string>) {
    return (
      <div className="panel">
        <div className="validation-errors">
          <ul>
            {this.renderErrorListItems(validationErrors)}
          </ul>
        </div>
      </div>
    );
  }
  
  renderAlerts() {
    console.log('renderAlerts called....');
    const alerts = this.state.alerts.map((alert: any, index: number) => {
      return this.renderAlert(alert.type, alert.text, index);
    });

    return (
      <div>{alerts}</div>
    );
  }

  renderAlert(type: any, text: string, index: number) {
    return (
      <Alert key={index} variant={type} dismissible>
        {text}
      </Alert>
    );
  }

  renderErrors() {
    const { validationErrors } = this.state;
    return validationErrors.size > 0 ? this.renderValidationErrors(validationErrors) : (null);
  }

  addAlert = (type: string, text: string, keepCurrentAlerts: boolean = false, autoClose?: number) => {
    console.log('add alert called....');
    const alert = { type, text };
    const alerts = keepCurrentAlerts ? [...this.state.alerts, alert] : [alert];
    this.setState({
      alerts
    });

    if (autoClose) {
      setTimeout(() => { this.clearAlerts(); }, autoClose);
    }
  }

  gender(client: Client, validationErrors: any, col1: number, col2: number) {
    return (
      <Form.Group as={Row} column="true" controlId="genderGroup">
        <Form.Label column sm={col1} className="labsmarts-form-check-label">Sex</Form.Label>
        <Col sm={col2}>
          <Form.Check
            className="form-check-inline-first"
            inline
            id="gender1"
            name="gender" 
            value={Gender.MALE}
            checked={client.gender === Gender.MALE}  
            onChange={this.handleInputChange}
            label="Male" 
            type="radio"
            isInvalid={!!validationErrors.get('gender')}
          />
          <Form.Check
            inline
            id="gender2"
            name="gender"
            value={Gender.FEMALE}
            checked={client.gender === Gender.FEMALE}  
            onChange={this.handleInputChange}
            label="Female" 
            type="radio"
            isInvalid={!!validationErrors.get('gender')}
          />
        </Col>
      </Form.Group>
    );
  }

  menopause(client: Client, validationErrors: any, col1: number, col2: number) {
    if (client.gender !== Gender.FEMALE) {
      // In case the user previously selected Female and set Menopause, we need to clear it
      // Also, the user may have selected a trimester so we need to clear it as well
      client.menopause = undefined;
      client.trimester = undefined;
      return null;
    }

    return (
      <Form.Group as={Row} controlId="menopauseGroup">
        <Form.Label column sm={col1} className="labsmarts-form-check-label">Menopause/Pregnant</Form.Label>
        <Col sm={col2}>
          <Form.Check
            className="form-check-inline-first"
            inline
            id="menopause1"
            name="menopause" 
            value={Menopause.PREMENOPAUSE}
            checked={client.menopause === Menopause.PREMENOPAUSE}  
            onChange={this.handleInputChange}
            label="Premenopause" 
            type="radio"
            isInvalid={!!validationErrors.get('menopause')}
          />
          <Form.Check
            inline
            id="menopause2"
            name="menopause"
            value={Menopause.POSTMENOPAUSE}
            checked={client.menopause === Menopause.POSTMENOPAUSE}  
            onChange={this.handleInputChange}
            label="Postmenopause" 
            type="radio"
            isInvalid={!!validationErrors.get('menopause')}
          />
          <Form.Check
            id="menopause3"
            inline
            name="menopause"
            value={Menopause.PREGNANT}
            checked={client.menopause === Menopause.PREGNANT}  
            onChange={this.handleInputChange}
            label="Pregnant" 
            type="radio"
            isInvalid={!!validationErrors.get('menopause')}
          />
        </Col>
      </Form.Group>
    );
  }

  trimester(client: Client, validationErrors: any, col1: number, col2: number) {
    if (client.menopause !== Menopause.PREGNANT) {
      // In case the user previously selected a trimester, we need to clear it
      client.trimester = undefined;
      return null;
    }

    // Default trimester so validation rules are satisfied
    if (client.trimester == null) {
      client.trimester = NaN;
    }
  
    return (
      <Form.Group as={Row} controlId="trimesterGroup">
        <Form.Label column sm={col1}>Trimester</Form.Label>
        <Col sm={col2}>
          <Form.Control as="select"
            name="trimester"
            value={client.trimester.toString()}
            onChange={this.handleInputChange}
            isInvalid={!!validationErrors.get('trimester')}
          >
            <option value="NaN">Select Trimester</option>
            <option value="1">First</option>
            <option value="2">Second</option>
            <option value="3">Third</option>
            <option value="0">Unknown</option>
          </Form.Control>
        </Col>  
      </Form.Group>
    );
  }

  dateOfBirth(client: Client, validationErrors: any, col1: number, col2: number) {
    return (
      <Form.Group as={Row} controlId="dateOfBirthGroup">
        <Form.Label column sm={col1}>Date of Birth</Form.Label>
        <Col sm={col2}>
          {/* TODO: Look into using a customInput here so that validation styling is applied. */}
          <DatePicker
            selected={this.state.dateOfBirth}
            onChange={this.handleDateOfBirthChange}
            maxDate={new Date()}
            peekNextMonth
            showMonthDropdown
            showYearDropdown
            todayButton
            dropdownMode="select"
            dateFormat="MM/dd/yyyy"
            placeholderText="MM/DD/YYYY"
            className="form-control"
            popperPlacement="bottom"
            popperModifiers={{
                flip: {
                    behavior: ["bottom"] // don't allow it to flip to be above
                },
                preventOverflow: {
                    enabled: false // tell it not to try to stay within the view (this prevents the popper from covering the element you clicked)
                },
                hide: {
                    enabled: false // turn off since needs preventOverflow to be enabled
                }
            }}
          />
        </Col>
      </Form.Group>
    );
  }

  ethnicity(client: Client, validationErrors: any, col1: number, col2: number) {
    return (
      <Form.Group as={Row} controlId="ethnicityGroup">
        <Form.Label column sm={col1}>
          Ethnicity&nbsp;
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip id="ethnicity-tooltip">
                Selecting an ethnicity applies population-specific reference ranges to a small set of markers where research shows meaningful differences.
                <br /><br />
                <strong>African American:</strong> Creatinine, Lipoprotein(a), Alkaline Phosphatase, Total Bilirubin, and PSA (Total &amp; Free).
                <br /><br />
                <strong>Hispanic:</strong> Total Bilirubin and PSA (Total &amp; Free).
                <br /><br />
                <strong>White, Asian, or Other:</strong> No ethnicity-specific adjustments.
              </Tooltip>
            }
          >
            <FontAwesomeIcon
              className="external-link"
              icon="info-circle"
              style={{ verticalAlign: 'middle', marginTop: '-2px' }}
            />
          </OverlayTrigger>
        </Form.Label>
        <Col sm={col2}>
          <Form.Control as="select"
            name="ethnicity"
            placeholder="Select Ethnicity"
            value={client.ethnicity}
            onChange={this.handleInputChange}
            isInvalid={!!validationErrors.get('ethnicity')}
          >
            <option value={Ethnicity.NONE}>Select Ethnicity</option>
            {
              Client.ETHNICITIES.map((ethnicity: Ethnicity, index: number) => {
                return (
                    <option key={index} value={ethnicity} >
                      {ethnicity}
                    </option>
                  );
                })
            }
          </Form.Control>
        </Col>  
      </Form.Group>
    );
  }

  elevation(client: Client, validationErrors: any, col1: number, col2: number) {
    let elevationString = '';
    if (client.elevation != null) {
      elevationString = client.elevation.toString();
    }

    const panelTooltip = (
      <Tooltip 
        id="modal-tooltip"
      >Click this icon to learn more about how elevation is used and how to look it up.
      </Tooltip>
    );

    return (
      <Form.Group as={Row} controlId="elevationGroup">
        <Form.Label column sm={col1}>
          Elevation&nbsp;
          <OverlayTrigger overlay={panelTooltip}>
            <FontAwesomeIcon className="external-link" icon="external-link-alt"  onClick={this.openElevationInfoWindow} />
          </OverlayTrigger>
        </Form.Label>
        <Col sm={col2}>
          <InputGroup>
            <Form.Control
                name="elevation"
                maxLength={5}
                onChange={this.handleInputChange}
                placeholder="e.g. 5212"
                type="text"
                value={elevationString}
                isInvalid={!!validationErrors.get('elevation')}
            />
            <InputGroup.Append>
              <InputGroup.Text>feet</InputGroup.Text>
            </InputGroup.Append>
          </InputGroup>
        </Col>
      </Form.Group>
    );
  }

  cigaretteSmoker(client: Client, validationErrors: any, col1: number, col2: number) {
    return (
      <Form.Group as={Row} controlId="cigaretteSmokerGroup">
      <Form.Label column sm={col1} className="labsmarts-form-check-label">
        Inhaled Smoke?&nbsp;
        <OverlayTrigger
          placement="right"
          overlay={
            <Tooltip id="inhaled-smoke-tooltip">
              Refers to regular inhalation of smoke from combusted sources, such as cigarettes, cannabis, cigars, pipes, hookah, or wood/fire smoke from fireplaces or wildfires. Does not include vaping.
              <br /><br />
              This field is used to assess whether inhaled smoke may be contributing to suboptimal blood oxygen delivery.
              <br /><br />
              Select "Yes" if the client has regular exposure to any of these combusted sources.
              <br /><br />
              When set to "Yes" and the RBC Insights panel has already detected Oxygen Insufficiency (elevated RBC with at least one of Hgb or Hct also elevated or optimal), the panel displays: "Inhaled Smoke is Likely Contributing to Oxygen Insufficiency."
            </Tooltip>
          }
        >
          <FontAwesomeIcon
          className="external-link"
          icon="info-circle"
          style={{ verticalAlign: 'middle', marginTop: '-2px' }}
          />
        </OverlayTrigger>
        <br></br>
        (e.g., Cigarettes, Cannabis, Wood)
        
      </Form.Label>
        <Col sm={col2}>
          <Form.Check
            className="form-check-inline-first"
            inline
            id="cigaretteSmoker1"
            name="cigaretteSmoker" 
            value="false"
            checked={client.cigaretteSmoker != null && !client.cigaretteSmoker}  
            onChange={this.handleInputChange}
            label="No" 
            type="radio"
            isInvalid={!!validationErrors.get('cigaretteSmoker')}
          />
          <Form.Check
            inline
            id="cigaretteSmoker2"
            name="cigaretteSmoker"
            value="true"
            checked={client.cigaretteSmoker}   
            onChange={this.handleInputChange}
            label="Yes" 
            type="radio"
            isInvalid={!!validationErrors.get('cigaretteSmoker')}
          /> 
        </Col>
      </Form.Group>
    );
  }

  cigarettesSmoked(client: Client, validationErrors: any, col1: number, col2: number) {
    return null;
  }
/* Removing the Cigarettes Smoked field when they select Yet - 04-28-26
  cigarettesSmoked(client: Client, validationErrors: any, col1: number, col2: number) {
    console.log(client);
    if (client.cigaretteSmoker == null || !client.cigaretteSmoker) {
      return null;
    }

    let cigarettesSmokedString = '';
    if (client.cigarettesSmoked != null) {
      cigarettesSmokedString = client.cigarettesSmoked.toString();
    }

    return (
      <Form.Group as={Row} controlId="cigarettesSmoked">
        <Form.Label column sm={col1}>Cigarettes Smoked</Form.Label>
        <Col sm={col2}>
          <InputGroup>
            <Form.Control
                name="cigarettesSmoked"
                size="sm"
                maxLength={2}
                onChange={this.handleInputChange}
                placeholder="e.g. 20"
                type="text"
                value={cigarettesSmokedString}
                isInvalid={!!validationErrors.get('cigarettesSmoked')}
            />
            <InputGroup.Append>
              <InputGroup.Text>per day</InputGroup.Text>
            </InputGroup.Append>
          </InputGroup>
        </Col>
      </Form.Group>
    );
  }*/

  alcoholDrinker(client: Client, validationErrors: any, col1: number, col2: number) {
    return (
      <Form.Group as={Row} controlId="alcoholDrinkerGroup">
        <Form.Label column sm={col1} className="labsmarts-form-check-label">
          Alcohol Drinker?&nbsp;
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip id="alcohol-drinker-tooltip">
                Refers to heavy consumption of alcoholic beverages such as beer, wine, liquor, or spirits.
                <br /><br />
                This field is used to assess whether chronic alcohol consumption may be contributing to anemia or suboptimal blood oxygen delivery.
                <br /><br />
                At 3–4 or more drinks per week sustained over weeks to months, alcohol can deplete folate, leading to elevated MCV and anemia (PMID: 68235050).
                <br /><br />
                Select "Yes" if the client is a heavy drinker (at least 3–4 drinks per week on an ongoing basis).
                <br /><br />
                When set to "Yes" and the RBC Insights panel has detected both anemia (Possible or higher probability) and an elevated MCV (large RBC size), "Heavy Alcohol Consumption" is added to the Possible Root Causes list in the RBC Insights panel.
              </Tooltip>
            }
          >
            <FontAwesomeIcon
              className="external-link"
              icon="info-circle"
              style={{ verticalAlign: 'middle', marginTop: '-2px' }}
            />
          </OverlayTrigger>
          <br></br>
          (e.g., Beer, Wine, Spirits)
        </Form.Label>
        <Col sm={col2}>
          <Form.Check
            className="form-check-inline-first"
            inline
            id="alcoholDrinker1"
            name="alcoholDrinker" 
            value="false"
            checked={client.alcoholDrinker != null && !client.alcoholDrinker}  
            onChange={this.handleInputChange}
            label="No" 
            type="radio"
            isInvalid={!!validationErrors.get('alcoholDrinker')}
          />
          <Form.Check
            inline
            id="alcoholDrinker2"
            name="alcoholDrinker"
            value="true"
            checked={client.alcoholDrinker}   
            onChange={this.handleInputChange}
            label="Yes" 
            type="radio"
            isInvalid={!!validationErrors.get('alcoholDrinker')}
          /> 
        </Col>
      </Form.Group>
    );
  }

  standardDrinks(client: Client, validationErrors: any, col1: number, col2: number) {
    return null;
  }

  unitsField(col1: number, col2: number) {
    const { units } = this.state;
    return (
      <Form.Group as={Row} controlId="unitsGroup">
        <Form.Label column sm={col1} className="labsmarts-form-check-label">Units</Form.Label>
        <Col sm={col2}>
          <Form.Check
            className="form-check-inline-first"
            inline
            id="units-us"
            name="units"
            label="US"
            type="radio"
            checked={units === 'US'}
            onChange={() => this.handleUnitsChange('US')}
          />
          <Form.Check
            inline
            id="units-si"
            name="units"
            label="SI"
            type="radio"
            checked={units === 'SI'}
            onChange={() => this.handleUnitsChange('SI')}
          />
          &nbsp;
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip id="units-tooltip">
                {siUnitsVisible() ? (
                  <>
                    Applies to height and weight, and to marker values
                    throughout this client&apos;s test results and reports.
                    <br /><br />
                    Overrides the default in Report Settings. Results are stored the
                    same way either way — only the display changes.
                  </>
                ) : (
                  <>
                    Currently only applies to height and weight.
                    <br /><br />
                    SI units for markers will be available in a future update.
                  </>
                )}
              </Tooltip>
            }
          >
            <FontAwesomeIcon
              className="external-link"
              icon="info-circle"
              style={{ verticalAlign: 'middle', marginTop: '-2px', paddingBottom: '1px' }}
            />
          </OverlayTrigger>
        </Col>
      </Form.Group>
    );
  }

  heightField(client: Client, validationErrors: any, col1: number, col2: number) {
    const { units, heightFt, heightIn } = this.state;

    return (
      <Form.Group as={Row} controlId="heightGroup">
        <Form.Label column sm={col1}>Height</Form.Label>
        <Col sm={units === 'SI' ? col2 : col2 + 2}>
          {units === 'US' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '78px' }}>
                <Select<HeightOption>
                  isSearchable={false}
                  maxMenuHeight={400}
                  onChange={this.handleHeightFtChange}
                  options={FT_OPTIONS}
                  styles={HEIGHT_SELECT_STYLES}
                  value={FT_OPTIONS.find(o => o.value === heightFt) ?? FT_OPTIONS[0]}
                />
              </div>
              <span className="input-group-text">ft</span>
              <div style={{ width: '78px' }}>
                <Select<HeightOption>
                  isSearchable={false}
                  maxMenuHeight={400}
                  onChange={this.handleHeightInChange}
                  options={IN_OPTIONS}
                  styles={HEIGHT_SELECT_STYLES}
                  value={IN_OPTIONS.find(o => o.value === heightIn) ?? IN_OPTIONS[0]}
                />
              </div>
              <span className="input-group-text">in</span>
            </div>
          ) : (
            <InputGroup>
              <Form.Control
                maxLength={3}
                onChange={this.handleHeightCmChange}
                placeholder="e.g. 170"
                style={{ maxWidth: '78px' }}
                type="text"
                value={client.height != null ? client.height.toString() : ''}
                isInvalid={!!validationErrors.get('height')}
              />
              <InputGroup.Append>
                <InputGroup.Text>cm</InputGroup.Text>
              </InputGroup.Append>
            </InputGroup>
          )}
        </Col>
      </Form.Group>
    );
  }

  weightField(client: Client, validationErrors: any, col1: number, col2: number) {
    const { units } = this.state;
    const isUS = units === 'US';
    const displayValue = client.weight != null
      ? (isUS ? client.weight.toString() : MathUtils.lbsToKg(client.weight).toString())
      : '';

    const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (val.trim() === '') {
        client.weight = undefined;
      } else {
        const num = Number(val);
        if (num > 999) return;
        client.weight = isUS ? num : MathUtils.kgToLbs(num);
      }
      this.clearAlerts();
    };

    return (
      <Form.Group as={Row} controlId="weightGroup">
        <Form.Label column sm={col1}>Weight</Form.Label>
        <Col sm={col2}>
          <InputGroup>
            <Form.Control
              maxLength={3}
              onChange={handleWeightChange}
              placeholder={isUS ? 'e.g. 150' : 'e.g. 68'}
              style={{ maxWidth: '78px' }}
              type="text"
              value={displayValue}
              isInvalid={!!validationErrors.get('weight')}
            />
            <InputGroup.Append>
              <InputGroup.Text>{isUS ? 'lbs' : 'kg'}</InputGroup.Text>
            </InputGroup.Append>
          </InputGroup>
        </Col>
      </Form.Group>
    );
  }

  toggleAddClient() {
    console.log('toggleAdd called....');
    const { clientEntryStore } = this.props;
    clientEntryStore.setExpandAddClient(!this.state.expandAddClient);
  }

  render() {
    const { panelTitle, validationErrors, expandAddClient } = this.state;
    const { client, currentUser } = this.props;
    const col1 = 3, col2 = 6;
    const expandAddClientIconName = expandAddClient ? 'chevron-up' : 'chevron-down';

    console.log(currentUser);
    return (
      <div>
        {this.renderAlerts()}
        <Container fluid>
          <Card>
            <Card.Header onClick={() => this.toggleAddClient()}>
            <FontAwesomeIcon icon={expandAddClientIconName} />&nbsp;&nbsp;{panelTitle}
            </Card.Header>
            { expandAddClient ?
            <>
            <Card.Body>
              {this.renderErrors()}
              <br/>
              <Form noValidate style={{ paddingLeft: '20px' }}>
                <Form.Group as={Row} column="true" controlId="firstNameGroup">
                  <Form.Label column sm={col1}>First Name</Form.Label>
                  <Col sm={3}>
                    <Form.Control
                        name="firstName"
                        size="sm"
                        maxLength={50}
                        onChange={this.handleInputChange}
                        placeholder="e.g. MaryAnn"
                        type="text"
                        value={client.firstName}
                        isInvalid={!!validationErrors.get('firstName')}
                    />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} column="true" controlId="lastNameGroup">
                  <Form.Label column sm={col1}>Last Name</Form.Label>
                  <Col sm={3}>
                    <Form.Control
                        name="lastName"
                        size="sm"
                        maxLength={50}
                        onChange={this.handleInputChange}
                        placeholder="e.g. Marks"
                        type="text"
                        value={client.lastName}
                        isInvalid={!!validationErrors.get('lastName')}
                    />
                  </Col>
                </Form.Group>
                {this.gender(client, validationErrors, col1, col2)}
                {this.menopause(client, validationErrors, col1, 4)}
                {this.trimester(client, validationErrors, col1, 3)}
                {this.dateOfBirth(client, validationErrors, col1, col2)}
                {this.ethnicity(client, validationErrors, col1, 3)}
                {this.elevation(client, validationErrors, col1, 3)}
                {this.cigaretteSmoker(client, validationErrors, col1, col2)}
                {this.cigarettesSmoked(client, validationErrors, col1, col2)}
                {this.alcoholDrinker(client, validationErrors, col1, col2)}
                {this.standardDrinks(client, validationErrors, col1, col2)}
                {this.unitsField(col1, col2)}
                {this.heightField(client, validationErrors, col1, col2)}
                {this.weightField(client, validationErrors, col1, col2)}
                <Form.Group as={Row}>
                  <Col sm={{ span: 9, offset: 3 }}>
                    <Button id="submitButton" type="submit" onClick={this.saveOrUpdate}>Save</Button>
                    &nbsp;&nbsp;&nbsp;
                    <Button id="clearButton" type="button" disabled={!client.isNew()}
                      variant="outline-primary" onClick={() => this.clearForm(true)}>Clear</Button>
                  </Col>
                </Form.Group>
              </Form>
            </Card.Body>
            <Card.Footer>
              <div className="important-note">Note: Changes made to an existing client here only apply to all future test results added for that client.</div>
            </Card.Footer>
            </>
            : null 
          }
          </Card>
        </Container>
      </div>
    );
  }
}
